import * as React from 'react';
import { useState } from 'react';
import type { Address } from '../../../../graphql/generated/graphql';
import { EditorProps } from '../types/editor-props';
import { EditAddressModal } from '../../../../modules/modals/EditAddressModal';
import { AddressFormatter } from '../formatters/AddressFormatter';

const isAddress = (value: unknown): value is Address => typeof value === 'object' && value !== null;

/**
 * Editor for structured Address values.
 * Renders the current address inline and opens an EditAddressModal for editing.
 * On save, the modal calls `onCommit` so the value is persisted immediately
 * (bypassing the EditableField Save/Cancel toolbar, which would be awkward
 * for a modal-based editor).
 */
export function AddressEditor({ value, onChange, onCommit, onCancel }: EditorProps) {
  const [open, setOpen] = useState(true); // open immediately on entering edit mode

  const addressValue = isAddress(value) ? (value as Address) : null;

  const handleSave = (next: Address | null) => {
    if (onCommit) {
      onCommit(next);
    } else {
      onChange(next);
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Surface dismissal so EditableField exits edit mode; otherwise the
    // formatter underneath can't reopen the modal on the next click.
    onCancel?.();
  };

  return (
    <>
      {/* Preview the current value so the EditableField toolbar has something to anchor to */}
      <span className='inline-block min-w-[1em]'>
        <AddressFormatter value={addressValue} />
      </span>
      <EditAddressModal
        open={open}
        value={addressValue}
        onClose={handleClose}
        onSave={handleSave}
      />
    </>
  );
}

// Storybook / dev-tools
(AddressEditor as React.FC).displayName = 'AddressEditor';
